import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { Filter, ChevronDown, ChevronUp, X, History } from 'lucide-react';
import { colors, spacing, typography, borderRadius, shadows, breakpoints } from '../styles/theme';
import EventFinderAPI from '../services/api';
import { Event, EventFilters, EventsResponse } from '../types/api';
import { getVenueName } from '../utils/eventFields';
import { compareEventLocalDates, localCalendarDateOffset } from '../utils/eventDateTime';
import { RECENT_PAST_DAYS } from '../utils/eventPresentation';
import EventCard from './EventCard';
import ListEventCard from './ListEventCard';

/** Deduplicate events by `eventId`, preserving original order. */
const dedupeEvents = (events: Event[]): Event[] => {
  const seen = new Set<number>();
  const out: Event[] = [];
  for (const e of events) {
    if (e == null || e.eventId == null) continue;
    if (seen.has(e.eventId)) continue;
    seen.add(e.eventId);
    out.push(e);
  }
  return out;
};

interface EventListProps {
  filters?: EventFilters;  // Changed from initialFilters to filters
  title?: string;
  showFilters?: boolean;
  pageSize?: number;
  viewMode?: 'grid' | 'list';
  onEventClick?: (event: Event) => void;
}

const ListContainer = styled.div`
  width: 100%;
`;

const ListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${spacing[6]};
  gap: ${spacing[4]};
  
  @media (max-width: ${breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
    gap: ${spacing[4]};
  }
`;

const ListTitle = styled.h2`
  font-size: ${typography.fontSizes['2xl']};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.primary};
  margin: 0;
  
  @media (max-width: ${breakpoints.md}) {
    font-size: ${typography.fontSizes.xl};
    text-align: center;
  }
`;

const FilterSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  
  @media (max-width: ${breakpoints.md}) {
    justify-content: center;
  }
  
  @media (max-width: ${breakpoints.sm}) {
    flex-direction: column;
    gap: ${spacing[2]};
  }
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[2]} ${spacing[4]};
  background: ${colors.white};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.lg};
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.primary};
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    border-color: ${colors.primary[300]};
    background: ${colors.primary[50]};
  }
  
  &.active {
    background: ${colors.primary[500]};
    color: ${colors.white};
    border-color: ${colors.primary[500]};
  }
`;

const FiltersDropdown = styled.div`
  position: relative;
`;

const FiltersPanel = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  width: 320px;
  background: ${colors.white};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.lg};
  box-shadow: ${shadows.lg};
  padding: ${spacing[4]};
  z-index: 100;
  margin-top: ${spacing[1]};
  
  @media (max-width: ${breakpoints.sm}) {
    width: 280px;
    right: -20px;
  }
`;

const FilterGroup = styled.div`
  margin-bottom: ${spacing[4]};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterLabel = styled.label`
  display: block;
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};
  color: ${colors.text.primary};
  margin-bottom: ${spacing[2]};
`;

const FilterInput = styled.input`
  width: 100%;
  padding: ${spacing[2]} ${spacing[3]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.base};
  font-size: ${typography.fontSizes.sm};
  
  &:focus {
    outline: none;
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 2px ${colors.primary[100]};
  }
`;

const FilterSelect = styled.select`
  width: 100%;
  padding: ${spacing[2]} ${spacing[3]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.base};
  font-size: ${typography.fontSizes.sm};
  background: ${colors.white};
  
  &:focus {
    outline: none;
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 2px ${colors.primary[100]};
  }
`;

const FilterCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  
  input {
    width: auto;
  }
  
  label {
    margin: 0;
    font-weight: ${typography.fontWeights.normal};
    cursor: pointer;
  }
`;

const EventGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${spacing[6]};
  margin-bottom: ${spacing[8]};
  
  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${spacing[4]};
  }
`;

const EventListView = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[8]};
`;

const LoadMoreButton = styled.button`
  display: block;
  margin: 0 auto;
  padding: ${spacing[3]} ${spacing[8]};
  background: ${colors.primary[500]};
  color: ${colors.white};
  border: none;
  border-radius: ${borderRadius.lg};
  font-size: ${typography.fontSizes.base};
  font-weight: ${typography.fontWeights.medium};
  cursor: pointer;
  transition: background-color 0.15s ease;
  
  &:hover {
    background: ${colors.primary[600]};
  }
  
  &:disabled {
    background: ${colors.gray[400]};
    cursor: not-allowed;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${spacing[12]};
`;

const LoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid ${colors.border.light};
  border-top: 3px solid ${colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${spacing[12]};
  color: ${colors.text.secondary};
`;

const EmptyMessage = styled.div`
  font-size: ${typography.fontSizes.lg};
  margin-bottom: ${spacing[2]};
`;

const EmptySubtext = styled.div`
  font-size: ${typography.fontSizes.sm};
`;

const ResultsInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${spacing[4]};
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.secondary};
  
  @media (max-width: ${breakpoints.sm}) {
    flex-direction: column;
    gap: ${spacing[2]};
    text-align: center;
  }
`;

// Collapsible "Recent past events" pane shown above the upcoming list.
const PastPaneContainer = styled.section`
  margin-bottom: ${spacing[6]};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.lg};
  background: ${colors.background.secondary};
  overflow: hidden;
`;

const PastPaneToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${spacing[3]} ${spacing[4]};
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};
  color: ${colors.text.primary};
  text-align: left;

  &:hover {
    background: ${colors.background.primary};
  }
`;

const PastPaneToggleLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
`;

const PastPaneCount = styled.span`
  font-weight: ${typography.fontWeights.normal};
  color: ${colors.text.secondary};
`;

const PastPaneBody = styled.div`
  padding: ${spacing[4]};
  border-top: 1px solid ${colors.border.light};
  background: ${colors.white};
`;

const PastPaneEmpty = styled.div`
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.secondary};
`;

export const EventList: React.FC<EventListProps> = ({
  filters: externalFilters = {},
  title = 'Events',
  showFilters = true,
  pageSize = 12,
  viewMode = 'grid',
  onEventClick
}) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [internalFilters, setInternalFilters] = useState<EventFilters>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  // Recent past events pane (events whose endDate is within the last
  // RECENT_PAST_DAYS days). Hidden/collapsed by default.
  const [pastEvents, setPastEvents] = useState<Event[]>([]);
  const [isPastExpanded, setIsPastExpanded] = useState(false);
  const [isLoadingPast, setIsLoadingPast] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    hasNextPage: false,
    totalItems: 0
  });

  // Memoize the active filters to prevent unnecessary re-renders
  const activeFilters = useMemo(() => {
    return { ...externalFilters, ...internalFilters };
  }, [externalFilters, internalFilters]);

  const loadEvents = useCallback(async (page: number = 1, append: boolean = false) => {
    // Skip initial load if we're waiting for external filters and showFilters is false
    if (!showFilters && Object.keys(activeFilters).length === 0) {
      return;
    }

    if (page === 1) {
      setIsLoading(true);
      // Drop the previous query's events before the new one lands so a slow or
      // failing request can never leave unrelated results on screen.
      setEvents([]);
      setLoadError(null);
    } else {
      setIsLoadingMore(true);
    }

    try {
      let response: EventsResponse;
      
      // Check if this is a vector search query
      if (activeFilters.searchQuery) {
        // Use vector search for text-based queries
        const { searchQuery, ...otherFilters } = activeFilters;
        
        // For vector search, we get a limited set of results and handle pagination client-side
        const vectorResponse = await EventFinderAPI.vectorSearch({
          searchTerms: searchQuery,
          filters: otherFilters,
          limit: Math.max(pageSize * 5, 50), // Get enough results for multiple pages
          threshold: 0.6, // Adjust relevance threshold as needed
          includeEventDetails: true
        });

        // Convert vector search results to EventsResponse format
        // Calculate pagination
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const currentPageResults = vectorResponse.results.slice(startIndex, endIndex);
        
        if (currentPageResults.length > 0) {
          // Convert vector search results directly to Event format
          const eventResults: Event[] = currentPageResults.map(result => ({
            eventId: result.databaseEventId,
            title: result.title,
            description: result.description || '',
            startDate: result.startDate || '',
            endDate: result.endDate || '',
            city: result.city || '',
            state: result.state || '',
            country: result.country || '',
            venue: getVenueName(result.venue),
            isVirtual: result.isVirtual || false,
            isPartnerHosted: result.isPartnerHosted || false,
            eventUrl: result.eventUrl || result.eventDetails?.[0]?.eventUrl || '',
            eventSources: [], // Vector search doesn't include event sources
            entity: result.eventDetails?.[0] ? {
              id: 0, // Vector search doesn't provide entity ID
              name: result.eventDetails[0].entityName,
              type: result.eventDetails[0].typeName
            } : undefined
          }));

          const totalResults = Math.min(vectorResponse.totalResults, vectorResponse.results.length);
          response = {
            pagination: {
              page: page,
              limit: pageSize,
              totalItems: totalResults,
              totalPages: Math.ceil(totalResults / pageSize),
              hasNextPage: endIndex < totalResults,
              hasPreviousPage: page > 1
            },
            filters: activeFilters,
            sort: { field: 'relevanceScore', direction: 'DESC' },
            events: eventResults
          };
        } else {
          // No results found
          response = {
            pagination: {
              page: 1,
              limit: pageSize,
              totalItems: 0,
              totalPages: 0,
              hasNextPage: false,
              hasPreviousPage: false
            },
            filters: activeFilters,
            sort: { field: 'relevanceScore', direction: 'DESC' },
            events: []
          };
        }
      } else if (activeFilters.eventType) {
        // Use category-based API for eventType filters. Use StartDate ASC
        // so the page reads chronologically from today onward.
        const { eventType, ...otherFilters } = activeFilters;
        response = await EventFinderAPI.getEventsByCategory(
          eventType,
          otherFilters,
          page,
          pageSize,
          'StartDate',
          'ASC'
        );
      } else if (activeFilters.useGeoCity && activeFilters.city) {
        // Geo-radius city search via the new `getEventsByCity` endpoint.
        // Resolves the city through the curated `worldcities` reference
        // dataset and matches events whose venue/location coordinates fall
        // within the radius — including events in adjacent municipalities
        // that a strict `City =` filter would miss.
        response = await EventFinderAPI.getEventsByCity(activeFilters.city, {
          state: activeFilters.state,
          country: activeFilters.country,
          radiusKm: activeFilters.radiusKm,
          upcomingOnly: activeFilters.upcomingOnly !== false,
          days: activeFilters.horizonDays,
          isVirtual: activeFilters.isVirtual,
          page,
          limit: pageSize,
          sortBy: 'startDate',
          sortDirection: 'ASC',
        });
      } else {
        // Use regular events API for other filters. Sort by StartDate
        // ascending so the upcoming list reads chronologically.
        response = await EventFinderAPI.getEvents(
          activeFilters,
          page,
          pageSize,
          'StartDate',
          'ASC'
        );
      }

      if (append) {
        setEvents(prev => dedupeEvents([...prev, ...response.events]));
      } else {
        setEvents(dedupeEvents(response.events));
      }

      setPagination({
        page: response.pagination.page,
        hasNextPage: response.pagination.hasNextPage,
        totalItems: response.pagination.totalItems
      });
    } catch (error) {
      console.error('Error fetching events:', error);
      if (!append) setEvents([]);
      setLoadError((error as Error)?.message || 'Something went wrong');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [activeFilters, pageSize, showFilters]);

  // Load events whenever the active filters change (or a retry is requested)
  useEffect(() => {
    loadEvents(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadEvents, retryToken]);

  // Load the "recent past events" pane (best effort) whenever the active
  // filters change. We only show this pane when the upcoming-only mode is
  // on — otherwise the main list already includes past events. Vector
  // search results are time-relevance-ranked, so we skip the pane there.
  useEffect(() => {
    const isUpcomingMode = activeFilters.upcomingOnly !== false;
    if (!isUpcomingMode || activeFilters.searchQuery) {
      setPastEvents([]);
      return;
    }

    let cancelled = false;
    const fetchPast = async () => {
      setIsLoadingPast(true);
      try {
        // Strip time constraints from the active filters and replace with
        // a narrow window covering the last RECENT_PAST_DAYS days. We
        // always use the regular `getEvents` endpoint here, regardless of
        // which path the upcoming list uses, because it accepts explicit
        // start/end-date filters and works across category/city
        // scenarios.
        const {
          upcomingOnly: _u,
          startDateFrom: _sf,
          startDateTo: _st,
          endDateFrom: _ef,
          endDateTo: _et,
          useGeoCity: _g,
          radiusKm: _r,
          ...rest
        } = activeFilters;
        void _u; void _sf; void _st; void _ef; void _et; void _g; void _r;

        const pastFilters: EventFilters = {
          ...rest,
          upcomingOnly: false,
          endDateFrom: localCalendarDateOffset(-RECENT_PAST_DAYS),
          endDateTo: localCalendarDateOffset(0),
        };

        const response = await EventFinderAPI.getEvents(
          pastFilters,
          1,
          20,
          'EndDate',
          'DESC'
        );
        if (cancelled) return;

        // Defensive client-side filter: only events that have actually
        // ended (endDate strictly before today's local midnight) and
        // ended within the recent window.
        const today = localCalendarDateOffset(0);
        const cutoff = localCalendarDateOffset(-RECENT_PAST_DAYS);
        const filtered = response.events.filter(e => {
          const beforeToday = compareEventLocalDates(e.endDate, today);
          const onOrAfterCutoff = compareEventLocalDates(e.endDate, cutoff);
          return beforeToday === -1 && onOrAfterCutoff !== null && onOrAfterCutoff >= 0;
        });
        setPastEvents(dedupeEvents(filtered));
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching recent past events:', err);
          setPastEvents([]);
        }
      } finally {
        if (!cancelled) setIsLoadingPast(false);
      }
    };

    fetchPast();
    return () => { cancelled = true; };
  }, [activeFilters]);

  const handleFilterChange = (key: keyof EventFilters, value: any) => {
    setInternalFilters(prev => ({
      ...prev,
      [key]: value === '' ? undefined : value
    }));
  };

  const handleLoadMore = () => {
    loadEvents(pagination.page + 1, true);
  };

  const clearFilters = () => {
    setInternalFilters({});
    setShowFiltersPanel(false);
  };

  // Combine external and internal filters for display purposes
  const displayFilters = { ...externalFilters, ...internalFilters };

  const hasActiveFilters = Object.values(internalFilters).some(value => 
    value !== undefined && value !== null && value !== ''
  );

  if (isLoading) {
    return (
      <ListContainer>
        <LoadingContainer>
          <LoadingSpinner />
        </LoadingContainer>
      </ListContainer>
    );
  }

  return (
    <ListContainer>
      <ListHeader>
        <ListTitle>{title}</ListTitle>
        {showFilters && (
          <FilterSection>
            <FiltersDropdown>
              <FilterButton
                className={showFiltersPanel ? 'active' : ''}
                onClick={() => setShowFiltersPanel(!showFiltersPanel)}
              >
                <Filter size={16} />
                Filters
                <ChevronDown size={16} />
              </FilterButton>
              
              {showFiltersPanel && (
                <FiltersPanel>
                  <FilterGroup>
                    <FilterLabel>Search</FilterLabel>
                    <FilterInput
                      type="text"
                      placeholder="Search events..."
                      value={displayFilters.entityName || ''}
                      onChange={(e) => handleFilterChange('entityName', e.target.value)}
                    />
                  </FilterGroup>
                  
                  <FilterGroup>
                    <FilterLabel>Location</FilterLabel>
                    <FilterInput
                      type="text"
                      placeholder="City"
                      value={displayFilters.city || ''}
                      onChange={(e) => handleFilterChange('city', e.target.value)}
                    />
                  </FilterGroup>
                  
                  <FilterGroup>
                    <FilterLabel>Event Type</FilterLabel>
                    <FilterSelect
                      value={displayFilters.eventType || ''}
                      onChange={(e) => handleFilterChange('eventType', e.target.value)}
                    >
                      <option value="">All Types</option>
                      <option value="Conference">Conference</option>
                      <option value="Webinar">Webinar</option>
                      <option value="Workshop">Workshop</option>
                      <option value="Meetup">Meetup</option>
                    </FilterSelect>
                  </FilterGroup>
                  
                  <FilterGroup>
                    <FilterCheckbox>
                      <input
                        type="checkbox"
                        id="virtualOnly"
                        checked={displayFilters.isVirtual || false}
                        onChange={(e) => handleFilterChange('isVirtual', e.target.checked || undefined)}
                      />
                      <FilterLabel htmlFor="virtualOnly">Virtual events only</FilterLabel>
                    </FilterCheckbox>
                  </FilterGroup>
                  
                  <FilterGroup>
                    <FilterCheckbox>
                      <input
                        type="checkbox"
                        id="upcomingOnly"
                        checked={displayFilters.upcomingOnly || false}
                        onChange={(e) => handleFilterChange('upcomingOnly', e.target.checked || undefined)}
                      />
                      <FilterLabel htmlFor="upcomingOnly">Upcoming events only</FilterLabel>
                    </FilterCheckbox>
                  </FilterGroup>
                  
                  {hasActiveFilters && (
                    <FilterButton onClick={clearFilters}>
                      <X size={16} />
                      Clear Filters
                    </FilterButton>
                  )}
                </FiltersPanel>
              )}
            </FiltersDropdown>
          </FilterSection>
        )}
      </ListHeader>

      {(activeFilters.upcomingOnly !== false) && !activeFilters.searchQuery && (pastEvents.length > 0 || isLoadingPast) && (
        <PastPaneContainer>
          <PastPaneToggle
            type="button"
            aria-expanded={isPastExpanded}
            onClick={() => setIsPastExpanded(prev => !prev)}
          >
            <PastPaneToggleLabel>
              <History size={16} />
              Recent past events
              <PastPaneCount>
                {isLoadingPast ? '(loading…)' : `(${pastEvents.length})`}
              </PastPaneCount>
            </PastPaneToggleLabel>
            {isPastExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </PastPaneToggle>
          {isPastExpanded && (
            <PastPaneBody>
              {pastEvents.length === 0 ? (
                <PastPaneEmpty>
                  No events ended in the last {RECENT_PAST_DAYS} days.
                </PastPaneEmpty>
              ) : viewMode === 'grid' ? (
                <EventGrid>
                  {pastEvents.map(event => (
                    <EventCard
                      key={`past-${event.eventId}`}
                      event={event}
                      onClick={onEventClick ? () => onEventClick(event) : undefined}
                    />
                  ))}
                </EventGrid>
              ) : (
                <EventListView>
                  {pastEvents.map(event => (
                    <ListEventCard
                      key={`past-${event.eventId}`}
                      event={event}
                      onClick={onEventClick ? () => onEventClick(event) : undefined}
                    />
                  ))}
                </EventListView>
              )}
            </PastPaneBody>
          )}
        </PastPaneContainer>
      )}

      <ResultsInfo>
        <div>
          Showing {events.length} of {pagination.totalItems} events
        </div>
        {hasActiveFilters && (
          <div>
            Filters applied
          </div>
        )}
      </ResultsInfo>

      {events.length === 0 ? (
        <EmptyState>
          {loadError ? (
            <>
              <EmptyMessage>We could not load these events</EmptyMessage>
              <EmptySubtext>{loadError}</EmptySubtext>
              <FilterButton type="button" onClick={() => setRetryToken((token) => token + 1)}>
                <History size={16} />
                Retry
              </FilterButton>
            </>
          ) : (
            <>
              <EmptyMessage>No events found</EmptyMessage>
              <EmptySubtext>
                Try adjusting your filters or search criteria
              </EmptySubtext>
            </>
          )}
        </EmptyState>
      ) : (
        <>
          {viewMode === 'grid' ? (
            <EventGrid>
              {events.map((event) => (
                <EventCard
                  key={event.eventId}
                  event={event}
                  onClick={onEventClick ? () => onEventClick(event) : undefined}
                />
              ))}
            </EventGrid>
          ) : (
            <EventListView>
              {events.map((event) => (
                <ListEventCard
                  key={event.eventId}
                  event={event}
                  onClick={onEventClick ? () => onEventClick(event) : undefined}
                />
              ))}
            </EventListView>
          )}
          
          {pagination.hasNextPage && (
            <LoadMoreButton
              onClick={handleLoadMore}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? 'Loading...' : 'Load More Events'}
            </LoadMoreButton>
          )}
        </>
      )}
    </ListContainer>
  );
};

export default EventList;
